import { inject } from '@angular/core'
import { throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { ToastService } from '@common/services'
import { AuthService } from '@services/auth.service'
import { environment as env } from '@envs/environment'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { Router } from '@angular/router'
import { AccountService } from '@services/account.service'

/** Códigos do visitante (spec 048) tratados sem toast. */
export const VISITOR_PROOF_REQUIRED = 'VISITOR_PROOF_REQUIRED'
export const VISITOR_SESSION_NOT_OPENED = 'VISITOR_SESSION_NOT_OPENED'
export const VISITOR_NOT_FOUND = 'VISITOR_NOT_FOUND'

/** Recusas que a tela do visitante mostra no próprio formulário, sem toast (spec 048). */
const VISITOR_INLINE_CODES = [
	'VISITOR_ALREADY_REGISTERED',
	'VISITOR_ALREADY_ACTIVE',
	'VISITOR_TERMS_OUTDATED',
	'VISITOR_ACTIVATION_NOT_ALLOWED',
	'VISITOR_PROOF_LIMIT',
	'VISITOR_PROOF_IN_PROGRESS',
	'VISITOR_PROOF_NOT_PENDING',
	'VISITOR_PROOF_ALREADY_VALIDATED',
	'VISITOR_PROOF_INVALID_SIDES'
]

/** Validação documental do fornecedor (spec 049): o estágio 2 leva à etapa de validação, sem toast. */
export const SUPPLIER_ACCESS_BLOCKED = 'SUPPLIER_ACCESS_BLOCKED'

/** Recusas que a etapa de validação do fornecedor mostra no próprio formulário, sem toast (spec 049). */
const SUPPLIER_VALIDATION_INLINE_CODES = [
	'SUPPLIER_PROOF_LIMIT',
	'SUPPLIER_PROOF_IN_PROGRESS',
	'SUPPLIER_PROOF_NOT_PENDING',
	'SUPPLIER_PROOF_INVALID_REQUEST',
	'SUPPLIER_VALIDATION_ALREADY_CONFIRMED',
	'SUPPLIER_VALIDATION_NO_REPRESENTATIVE',
	'SUPPLIER_VALIDATION_DISABLED',
	'SUPPLIER_RESPONSIBLE_DUPLICATED'
]

export const ErrorInterceptor: HttpInterceptorFn = (request, next) => {
	const modal = inject(NgbModal)
	const auth = inject(AuthService)
	const toast = inject(ToastService)
	const router = inject(Router)
	const accountService = inject(AccountService)

	const accounts = request.url.includes(env.url_customer_api.replace('https://', ''))
	const payments = request.url.includes(env.url_payment_api.replace('https://', ''))
	const screenshot = request.url.includes(env.url_screenshot_api.replace('https://', ''))
	const attachments = request.url.includes(env.url_attachment_api.replace('https://', ''))
	const notifications = request.url.includes(env.url_notification_api.replace('https://', ''))
	const signatures = request.url.includes(env.url_document_signature_api.replace('https://', ''))
	const documents = request.url.includes(env.url_document_generator_api.replace('https://', ''))
	const refresh = request.url.includes('/v2/refresh')
	const devices = request.url.includes('/devices')
	// Cadastro público de cidadão (spec 048): a página mostra no formulário as recusas do BFF.
	const portalBff = !!env.url_portal_bff && request.url.startsWith(env.url_portal_bff)

	if (
		portalBff ||
		devices ||
		refresh ||
		payments ||
		documents ||
		signatures ||
		accounts ||
		attachments ||
		notifications ||
		screenshot ||
		request.url.includes('assets') ||
		request.url.includes('prompts')
	) {
		return next(request)
	}

	if (request.method === 'POST' && request.url.includes(`${env.url_management_api}/products`.replace('https://', '').replace('http://', ''))) {
		return next(request)
	}

	return next(request).pipe(
		catchError((err: HttpErrorResponse) => {
			if (err.status === 0 || err.status === 503) {
				return throwError(() => err)
			}

			const pathname = window.location.pathname

			let bypass = false

			if (pathname.includes('signin') && err.status === 401) {
				toast.error('Verifique o e-mail e senha informados!')
				return throwError(() => err)
			}

			let message = err.message

			if (VISITOR_INLINE_CODES.includes(err?.error?.code) || SUPPLIER_VALIDATION_INLINE_CODES.includes(err?.error?.code)) {
				return throwError(() => err)
			}

			switch (err.status) {
				case 400:
					message = err?.error?.message || 'A solicitação realizada possuí dados inválidos, verifique antes de enviar novamente.'

					if (err?.error?.errors) {
						const nextError = err?.error?.errors[0]
						message = (typeof nextError === 'string' ? nextError : nextError?.message) || message
					}

					if (message.toLowerCase().includes('registro informado já consta como cadastrado')) {
						bypass = true
					}

					break
				case 401:
					message = err?.error?.message || 'Sessão expirada, favor realizar login novamente.'
					auth.resetSession(true).then()
					modal.dismissAll()
					break
				case 403: {
					const code = err?.error?.code

					message = err?.error?.message || 'Usuário sem permissão para executar essa ação.'

					// Visitante sem a comprovação validada: o servidor recusa o quadro e a sessão (RF 36), e a tela
					// leva à etapa de envio, sem toast.
					if (code === VISITOR_PROOF_REQUIRED) {
						bypass = true
						router.navigate(['/visitor/proof'], { queryParams: { redirect: router.url } }).then()
					} else if (code === SUPPLIER_ACCESS_BLOCKED) {
						// Estágio 2 da validação do fornecedor: a etapa de validação explica e recebe os documentos.
						bypass = true

						if (!router.url.includes('/supplier/validation')) {
							router.navigate(['/supplier/validation']).then()
						}
					} else if (code === VISITOR_SESSION_NOT_OPENED) {
						// A tela mostra o card da sessão não aberta (RF 25).
						bypass = true
					} else if (accountService.currentProductVisitor) {
						// Rede de segurança: uma leitura que escape do modo leitura não vira enxurrada de toasts.
						bypass = true
						console.warn('[visitante] leitura recusada pelo servidor', request.url)
					}

					break
				}
				// Conflito com o estado do registro (paginação em andamento, simulação vencida, livro ainda não pronto) e
				// recurso que expirou: o servidor diz o que fazer. Sem isto o toast mostrava o texto técnico do HttpClient.
				case 409:
					message = err?.error?.message || 'A operação não pode ser feita no estado atual. Atualize a tela e tente de novo.'
					break
				case 410:
					message = err?.error?.message || 'O recurso não está mais disponível.'
					break
				case 404:
					message = err?.error?.message || 'Recurso não encontrado!'
					// Login com o papel e sem o registro: o guard leva à ativação.
					bypass = err?.error?.code === VISITOR_NOT_FOUND
					break
				case 500:
					message = 'Tivemos um problema ao se comunicar com nossos serviços, tente novamente.'
					break
			}

			if (!bypass) {
				toast.error(message)
			}

			return throwError(() => err)
		})
	)
}
